import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, unref as _unref, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  id: "list-medical-option",
  class: "main-content-wrapper"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h1", { class: "main-content-page-title" }, "オプションメニュー", -1)
const _hoisted_3 = { class: "list-medical-option-body" }

import moment from "moment";
import { onBeforeMount, ref, inject, computed } from "vue";
import { useStore } from "vuex";
import { get, set } from "@vueuse/core";
import { PageState } from "primevue/paginator";
import { IMedicalOption } from "@/clinic/typings";
import { MedicalOptionServices } from "@/clinic/services";
import type { ApiBasePaginatorReq, ApiBaseReq, ApiBaseReqParams, ApiMedicalOptionIndexRes } from "@/clinic/typings";
import Header from "@/clinic/components/atoms/Grid/header.vue";
import Grid from "@/clinic/components/atoms/Grid/index.vue";
import Paginator from "@/clinic/components/atoms/Paginator/index.vue";
import RouterUtils from "@/common/utils/router.util";
import RouterConstants from "@/clinic/constants/router.constants";
import { AxiosResponse } from "axios";
import { DEFAULT_PAGINATOR_ROWS } from "@/clinic/typings/constants/paginator";
import NumberUtils from "@/clinic/utils/number-utils";
import { ClinicPartner } from "@/clinic/typings/enums/clinic";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const store = useStore();

const medicalOptions = ref<IMedicalOption[]>([]);
const totalCount = ref(0);
const clinicId = ref(0);
const first = ref(0);
const searchTerm = ref("");
const rows = ref(DEFAULT_PAGINATOR_ROWS);
const page = ref(0);
const pgTop = ref<InstanceType<typeof Paginator> | null>(null);
const pgBot = ref<InstanceType<typeof Paginator> | null>(null);
const columns = ref([
  { field: "id", header: "ID", class: "col-1" },
  { field: "medical_menu_ids", header: "メニューID", class: "col-1" },
  { field: "name", header: "名前" },
  { field: "description", header: "説明" },
  { field: "time_unit", header: "所要時間" },
  { field: "unit_price", header: "単価" },
  { field: "created_at", header: "作成日" },
  { field: "position_number", header: "順番" },
]);

const isUnaffiliated = () => {
  return store.getters["authClient/selectedClinic"].partner == ClinicPartner.Unaffiliated;
};

const clinicIdParams = async (): Promise<ApiBaseReq> => ({ clinic_id: get(await get(clinicId)) });

const paginatorParams = computed(
  (): ApiBasePaginatorReq =>
    ({
      term: get(searchTerm),
      page: get(page) + 1, // バックエンドは1ベースのページ番号を期待
      rows: get(rows),
    } as ApiBasePaginatorReq)
);

const loadMedicalOptions = async () => {
  const { data } = (await MedicalOptionServices.getOptions(
    { ...(await clinicIdParams()), ...get(paginatorParams) } as ApiBaseReqParams,
    () => undefined,
    () => undefined
  )) as unknown as AxiosResponse<ApiMedicalOptionIndexRes>;

  const { options: optionsData, meta } = get(data);
  const { total_count } = meta;

  set(
    medicalOptions,
    optionsData.map(
      option =>
        ({ ...option, created_at: moment(option.created_at).format("DD/MM/YYYY HH:MM") } as unknown as IMedicalOption)
    )
  );
  set(totalCount, get(total_count));
};

const paging = async (fromId: string, { page: newPage, first: newFirst }: PageState) => {
  // 現在のページと同じ場合は何もしない
  if (newPage === get(page)) return;

  // ページ番号を更新
  set(page, newPage);

  // firstの値を更新（PrimeVueから渡された値を使用）
  set(first, newFirst);

  await loadMedicalOptions();
};

const onSearch = async (event: Event) => {
  const target = event.currentTarget as HTMLInputElement;
  set(searchTerm, target?.value || "");
  // 検索時はページをリセット
  set(page, 0);
  set(first, 0);
  await loadMedicalOptions();
};

const movetoNewMedicalOption = () => {
  RouterUtils.goTo(RouterConstants.MEDICAL_OPTIONS_NEW_ROUTER_NAME);
};

const onItemClick = (event: Event, item: Record<string, unknown>) => {
  if (isUnaffiliated()) {
    return RouterUtils.goToWithParams(RouterConstants.MEDICAL_OPTIONS_EDIT_ROUTER_NAME, { id: item.id });
  }
};

const loadList = async () => {
  set(clinicId, await inject("clinicId"));
  await loadMedicalOptions();
};

onBeforeMount(() => loadList());

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createVNode(Header, {
      enableSearch: "",
      onSearch: onSearch,
      onNew: movetoNewMedicalOption,
      createButtonLabel: "新規作成",
      searchPlaceholder: "オプションメニュー名",
      enableCreate: isUnaffiliated()
    }, null, 8, ["enableCreate"]),
    _createElementVNode("div", _hoisted_3, [
      (totalCount.value > rows.value)
        ? (_openBlock(), _createBlock(Paginator, {
            key: 0,
            id: "pg-top",
            ref_key: "pgTop",
            ref: pgTop,
            modelValue: first.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((first).value = $event)),
            onPaging: paging,
            totalRecords: totalCount.value,
            rows: rows.value,
            paginatorId: "pg-top"
          }, null, 8, ["modelValue", "totalRecords", "rows"]))
        : _createCommentVNode("", true),
      _createVNode(Grid, {
        items: medicalOptions.value,
        columns: columns.value,
        onOnItemClick: onItemClick
      }, {
        medical_menu_ids: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(data.medical_menu_ids.join(", ")), 1)
        ]),
        unit_price: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(_unref(NumberUtils).formatJPCurrency(parseInt(data.unit_price), "")) + "円", 1)
        ]),
        created_at: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(_unref(moment)(data.created_at, "DD/MM/YYYY").format("YYYY/MM/DD")), 1)
        ]),
        position_number: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(Number(parseInt(data.position_number)).toLocaleString()), 1)
        ]),
        _: 1
      }, 8, ["items", "columns"]),
      (totalCount.value > rows.value)
        ? (_openBlock(), _createBlock(Paginator, {
            key: 1,
            id: "pg-bot",
            ref_key: "pgBot",
            ref: pgBot,
            modelValue: first.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((first).value = $event)),
            onPaging: paging,
            totalRecords: totalCount.value,
            rows: rows.value,
            class: "mt-2",
            paginatorId: "pg-bot"
          }, null, 8, ["modelValue", "totalRecords", "rows"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})